import React, { useState, useMemo } from "react"
import {
    DataGrid,
    GridColumnMenuColumnsItem,
    GridColumnMenuContainer,
    GridColumnMenuFilterItem,
    GridColumnMenuSortItem,
    useGridApiRef,
} from "@mui/x-data-grid"
import PropTypes from "prop-types"
import { styled } from "@mui/material/styles"
import { Box, Typography } from "@mui/material"
import { ReactComponent as TableBaseIcon } from "src/v2/assets/icons/table-base-icon.svg"
import { localeBRDataGridText } from "src/utils/data-grid-local-text"
import { deepmerge } from "@mui/utils"
import CustomLoadingOverlay from "./custom-loading-overlay"

const StyledGridColumnMenuContainer = styled(GridColumnMenuContainer)(() => ({
    backgroundColor: "#2E3448",
    "& .MuiMenuItem-root": {
        "& .MuiTypography-root": {
            fontSize: "14px",
            color: "#fff",
        },
        "&:hover": { backgroundColor: "#14192B" },
    },
    "& .MuiListItemIcon-root": {
        color: "#E3A107",
    },
}))

function CustomColumnMenu(props) {
    const itemProps = { colDef: props.colDef, onClick: props.hideMenu }

    return (
        <StyledGridColumnMenuContainer {...props}>
            <GridColumnMenuSortItem {...itemProps} />
            <GridColumnMenuFilterItem {...itemProps} />
            <GridColumnMenuColumnsItem {...itemProps} />
        </StyledGridColumnMenuContainer>
    )
}

CustomColumnMenu.propTypes = {
    colDef: PropTypes.object.isRequired,
    hideMenu: PropTypes.func.isRequired,
}

const NoRowOverlay = () => (
    <Box
        sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            color: "white",
            gap: "12px",
        }}
    >
        <TableBaseIcon />
        <Typography variant="textBody" sx={{ color: "#FFF !important" }}>
            Nenhum dado encontrado.
        </Typography>
    </Box>
)

const DataGridScrollTable = ({
    columns,
    rows,
    onRowClick,
    loading,
    pointerCursor,
    height = 500,
    sx,
    ...props
}) => {
    const apiRef = useGridApiRef()
    const [filterModel, setFilterModel] = useState({ items: [] })

    const filteredRows = useMemo(() => {
        return rows.filter((row) => {
            return filterModel.items.every((f) => {
                if (!f.value) return true
                const cellValue = String(row[f.field] ?? "").toLowerCase()
                return cellValue.includes(String(f.value).toLowerCase())
            })
        })
    }, [rows, filterModel])

    const baseSx = {
        width: "100%",

        "&.MuiDataGrid-root": {
            border: "none !important",
        },

        "& .MuiDataGrid-withBorderColor": {
            borderColor: "transparent !important",
        },

        "& .MuiDataGrid-columnHeaders": {
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: "#2E3448",
            borderBottom: "none !important",
        },

        "& .MuiDataGrid-columnSeparator": {
            display: "none !important",
        },

        "& .MuiDataGrid-cell": {
            fontFamily: "'Roboto', sans-serif",
            fontSize: "14px",
            color: "#FFFFFF !important",
            borderBottom: "none !important",
        },

        "& .MuiDataGrid-columnHeaderTitle": {
            fontFamily: "'Titillium Web', sans-serif",
            fontWeight: 700,
            fontSize: "11px",
            textTransform: "uppercase",
            color: "#FFFFFF",
        },

        "& .MuiDataGrid-row": {
            border: "none",
        },

        "& .even": { backgroundColor: "rgba(255,255,255,0.04)" },
        "& .odd": { backgroundColor: "rgba(255, 255, 255, 0.09)" },

        "& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover": {
            backgroundColor: "rgba(255,255,255,0.09)",
        },

        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
            outline: "none",
        },

        ...(pointerCursor && {
            "& .pointerCursor .MuiDataGrid-cell": {
                cursor: "pointer",
            },
        }),
    }

    const mergedSx = deepmerge(baseSx, sx || {})

    return (
        <Box sx={{ width: "100%" }}>
            <DataGrid
                apiRef={apiRef}
                rows={loading ? [] : filteredRows}
                columns={columns}
                loading={loading}
                autoHeight={false}
                hideFooter
                disableVirtualization={false}
                localeText={localeBRDataGridText}
                onRowClick={onRowClick}
                filterModel={filterModel}
                onFilterModelChange={setFilterModel}
                getRowClassName={(params) => {
                    const base =
                        params.indexRelativeToCurrentPage % 2 === 0
                            ? "even"
                            : "odd"
                    return pointerCursor ? base + " pointerCursor" : base
                }}
                slots={{
                    columnMenu: CustomColumnMenu,
                    noRowsOverlay: NoRowOverlay,
                    loadingOverlay: (props) => (
                        <CustomLoadingOverlay columns={columns} {...props} />
                    ),
                }}
                sx={{
                    ...mergedSx,
                    height,
                }}
                {...props}
            />
        </Box>
    )
}

DataGridScrollTable.propTypes = {
    columns: PropTypes.array.isRequired,
    rows: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    onRowClick: PropTypes.func,
    pointerCursor: PropTypes.bool,
    height: PropTypes.number,
    sx: PropTypes.object,
}

export default DataGridScrollTable

